
import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Edit, Trash2, Tag, ExternalLink } from 'lucide-react';
import { MerchandiseProduct } from '@/types/merchandise';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';

const ProductsList = () => {
  // Mock data - would be replaced with API data
  const mockProducts: MerchandiseProduct[] = [
    {
      id: '1',
      name: 'Logo T-Shirt',
      description: 'Classic band logo tee',
      price: 24.99,
      currency: 'USD',
      imageUrl: '/placeholder.svg',
      inventory: 45,
      category: 'Apparel',
      platform: 'shopify',
      externalId: 'gid://shopify/Product/123456789',
      externalUrl: 'https://mystore.myshopify.com/products/logo-t-shirt'
    },
    {
      id: '2',
      name: 'Album Vinyl',
      description: 'Limited edition vinyl',
      price: 29.99,
      currency: 'USD',
      imageUrl: '/placeholder.svg',
      inventory: 15,
      category: 'Music',
      platform: 'woocommerce',
      externalId: '987654',
      externalUrl: 'https://mystore.com/product/album-vinyl'
    },
    {
      id: '3',
      name: 'Tour Poster',
      description: 'Tour poster - signed',
      price: 19.99,
      currency: 'USD',
      imageUrl: '/placeholder.svg',
      inventory: 30,
      category: 'Collectibles',
      platform: 'internal'
    }
  ];

  const [products, setProducts] = useState<MerchandiseProduct[]>(mockProducts);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEditProduct = (productId: string) => {
    // Would implement edit product functionality here
    console.log('Edit product:', productId);
  };

  const handleDeleteProduct = (productId: string) => {
    // Would implement delete product functionality here
    console.log('Delete product:', productId);
    setProducts(products.filter(product => product.id !== productId));
  };

  const getPlatformBadgeColor = (platform: string) => {
    switch (platform) {
      case 'shopify': return 'bg-green-100 text-green-800';
      case 'woocommerce': return 'bg-purple-100 text-purple-800';
      case 'internal': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="relative w-64">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Tag className="h-4 w-4 mr-2" />
            Filter
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Inventory</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product) => (
                <TableRow key={product.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-md bg-gray-100 overflow-hidden">
                        <img 
                          src={product.imageUrl} 
                          alt={product.name} 
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <span>{product.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{product.currency} {product.price.toFixed(2)}</TableCell>
                  <TableCell>{product.inventory}</TableCell>
                  <TableCell>{product.category}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPlatformBadgeColor(product.platform)}`}>
                      {product.platform.charAt(0).toUpperCase() + product.platform.slice(1)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {product.externalUrl && (
                        <Button variant="ghost" size="icon" asChild>
                          <a href={product.externalUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => handleEditProduct(product.id)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteProduct(product.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                  No products found. Try adjusting your search.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

export default ProductsList;
